import React, { useEffect } from "react";
import { X } from "lucide-react";
import PennText from "@/components/ui/text/PennText";

type AdminModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
};

export const AdminModal = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = "max-w-lg",
}: AdminModalProps) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full ${maxWidth} max-h-[90vh] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl shadow-gray-300/40 dark:shadow-gray-950/60 flex flex-col overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <PennText as="h2" size="2xl" font="semibold" color="blue_grey1">
            {title}
          </PennText>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-blue_grey4 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue_grey2 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">{children}</div>
        {/* Footer */}
        {footer && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 flex flex-wrap justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
